import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {when} from "lit/directives/when.js";
import {get, translate} from "lit-translate";
import {container} from "tsyringe";
import type {RteValue} from "../../../domain/rte-value.type";
import {RteValueHelper} from "../../../helpers/rte-value.helper";
import type {HaRteComponent} from "../../ha-rte/ha-rte.component";
import {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {LinkTileHelper, type LinkTileDraft} from "../../../helpers/link-tile.helper";
import type {LinkTile} from "../../../domain/entities/link-tile.entity";
import {LINK_TILE_ICONS} from "../../../domain/entities/link-tile-icon.const";
import type {ComboboxItem} from "../../_inputs/ha-combobox/combobox-item.interface";
import type {RadioButtonItem} from "../../_inputs/ha-input-checkbox/radio-button-item.interface";
import {UiHelper} from "../../../helpers/ui.helper";
import {
    ALLOWED_IMAGE_MIME_TYPES,
    MAX_ASSET_UPLOAD_BYTES,
    MAX_ASSET_UPLOAD_MIB,
} from "../../../connectivity/asset-upload.const";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import type {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {ExternalLinkTarget} from "../../../domain/entities/link-targets/external-link-target";
import {KnowledgeObjectLinkTarget} from "../../../domain/entities/link-targets/knowledge-object-link-target";

const DEFAULT_TILE_COLOR = `#ffffff`;

/** UI-level link-kind choice — a finer split than `LinkTargetKind` (Anke,
 *  2026-09-05: "auch die rte-dokumente... alles andere genauso wie beim
 *  richtigen whiteboard"): 'whiteboard' and 'document' both build a
 *  `KnowledgeObjectLinkTarget` (`LinkTargetKind` 'knowledge-object'), just
 *  with a different `knowledgeObjectType` and a different backing picker
 *  list — `LinkTargetKind` itself (the `LinkTarget` subclass discriminator)
 *  is untouched. 'none' (Anke, 2026-09-07: "Keinen Link setzen") builds no
 *  target at all — `LinkTargetKind` has no member for it either, since a
 *  link-less tile is already modeled end-to-end as `target` being unset
 *  (rendered as `LinkTilePlainComponent`, see `dashboard-creator.component`). */
type LinkKindOption = 'none' | 'external' | 'whiteboard' | 'document';

// Exported (review 0030-04, MINOR) so the radio options themselves — not
// just their downstream effect on `_linkKind`/the saved `target` — can be
// asserted directly in tests.
export const LINK_KIND_ITEMS: ReadonlyArray<RadioButtonItem<LinkKindOption>> = [
    {id: 'none', value: 'none', label: 'Keinen Link setzen'},
    {id: 'external', value: 'external', label: 'Externe URL'},
    {id: 'whiteboard', value: 'whiteboard', label: 'Whiteboard-Dokument'},
    {id: 'document', value: 'document', label: 'RTE-Dokument'},
];

/**
 * Create/edit dialog for one Linkview dashboard tile (Anke, 2026-09-04):
 * text (required, colour-changeable), an optional link target covering the
 * whole tile — none, an external URL, a Whiteboard document, or an RTE document
 * (Anke, 2026-09-04: "ein komplexes Objekt unterschiedlicher Klasse... die
 * serialisierbar sind", see `link-targets/link-target.ts`; the latter two
 * added 2026-09-05, "alles andere genauso wie beim richtigen whiteboard") —
 * an optional image, the tile's background colour, and an optional Font
 * Awesome Sharp icon shown above the text.
 *
 * Structured exactly like the other non-entity dialogs in this codebase
 * (`ha-dialog-knowledge-object-edit` is the closest sibling: plain
 * `AbstractDialogComponent`, not `AbstractEntityDialogComponent` — a
 * LinkTile is a local Gridstack-positioned value object with no backend
 * repository/CommandBus yet, so the entity-form-binding machinery those
 * dialogs use doesn't apply here) — same `ha-form-<name>` wrapper, `.save`/
 * `.cancel` footer button classes, `data-type` field markers, and
 * `DialogHelper.closeDialog()` + CLOSE-command teardown on save.
 *
 * Unlike `ha-dialog-knowledge-object-edit`, this dialog does NOT persist
 * anything itself (no repository/CommandBus to persist THROUGH yet) — it
 * dispatches the built `LinkTileDraft` as the payload of an OKAY command;
 * `dashboard-creator.component.ts` (the caller, which owns Gridstack and
 * localStorage persistence) is the one that turns that into a new or
 * updated `LinkTile`.
 */
@customElement(`ha-dialog-link-tile-edit`)
export class HaDialogLinkTileEditComponent extends AbstractDialogComponent {

    /** `null` = create mode. Set = edit mode, pre-fills every field. */
    @property({type: Object})
    public tile: LinkTile | null = null;

    /**
     * The tile's label, as an `RteValue` (`Delta | RichTextDocument`, see its
     * own doc comment) — the rich-text field (Anke, 2026-09-07) replaces the
     * old plain-text field, and its own colour/highlight formatting is what
     * the earlier separate `ha-input-color` "Textfarbe" field used to be
     * for, so that field is gone too.
     */
    @state()
    private _text: RteValue = RteValueHelper.empty();

    @state()
    private _linkKind: LinkKindOption = 'none';

    @state()
    private _url = ``;

    @state()
    private _koItems: ComboboxItem[] = [];

    @state()
    private _selectedKoItem: ComboboxItem | null = null;

    @state()
    private _koPickerLoading = false;

    /** Set when the tile's saved target is a `KnowledgeObjectLinkTarget`
     *  whose `uid` is no longer in the fetched picker list (deleted
     *  meanwhile) — shows a hint instead of silently dropping the value. */
    @state()
    private _orphanedKoTitle: string | null = null;

    /** Set when the tile's saved target has a `kind` neither radio option
     *  builds (an `UnknownLinkTarget`, e.g. from a future link kind this
     *  version doesn't know) — shows a hint; saving replaces it as usual. */
    @state()
    private _unknownTargetKind: string | null = null;

    /** The picked file rendered as a data URL, for the PREVIEW only — never
     *  persisted, and dropped as soon as the file has been uploaded. */
    @state()
    private _imageDataUrl: string | null = null;

    /** The file the user picked, handed on unchanged. This dialog uploads
     *  NOTHING: the board's save button does, once the user has actually
     *  committed to the change. The asset store has no delete path, so an
     *  upload here would leave litter for every dialog anyone cancels. */
    @state()
    private _imageFile: File | null = null;

    /** The asset-store reference of the image the tile ALREADY has. Kept so
     *  reopening the dialog and saving without touching the image does not
     *  silently drop it — and cleared the moment a new file is picked. */
    @state()
    private _imageAssetUid: string | null = null;

    @state()
    private _color = DEFAULT_TILE_COLOR;

    @state()
    private _icon: string | null = null;

    /** Guards `_loadKnowledgeObjectItems`'s async picker-list load against a
     *  stale resolution after `tile` (or the selected radio kind) changed
     *  again (`DialogHelper` reuses the same dialog instance while one is
     *  already open — a fast double-click on two different tiles' edit
     *  buttons can change `tile` mid-request; switching the radio kind
     *  mid-request is the same kind of race). */
    private _pickerLoadGeneration = 0;

    public constructor() {
        super();
        this.closable = true;
    }

    private get _isSubmitEnabled(): boolean {
        if (!LinkTileHelper.isValidDraft(this._text)) return false;
        return this._linkKind === 'none' || this._linkKind === 'external' || this._selectedKoItem !== null;
    }

    protected async willUpdate(changedProperties: PropertyValues): Promise<void> {
        if (!changedProperties.has(`tile`)) {
            return;
        }

        this._text = this.tile?.text ?? RteValueHelper.empty();
        this._imageDataUrl = null;
        this._imageFile = null;
        this._imageAssetUid = this.tile?.imageAssetUid ?? null;
        this._color = this.tile?.color ?? DEFAULT_TILE_COLOR;
        this._icon = this.tile?.icon ?? null;
        this.title = get(this.tile
            ? `linkview.dialog.link-tile-edit.header-edit`
            : `linkview.dialog.link-tile-edit.header-create`);

        const target = this.tile?.target ?? null;
        this._url = target instanceof ExternalLinkTarget ? target.url : ``;
        this._orphanedKoTitle = null;
        this._unknownTargetKind = null;
        this._koItems = [];
        this._selectedKoItem = null;
        if (target instanceof KnowledgeObjectLinkTarget
            && (target.knowledgeObjectType === 'whiteboard' || target.knowledgeObjectType === 'document')) {
            this._linkKind = target.knowledgeObjectType;
        } else if (target instanceof ExternalLinkTarget) {
            // Review 0030-03, MAJOR: this branch used to sit AFTER the
            // generic `else if (target)` catch-all below, so it was
            // unreachable dead code — `target` is truthy for EVERY
            // ExternalLinkTarget too, so the catch-all ran first and
            // wrongly flagged every externally-linked tile's target as
            // "unsupported" (`_unknownTargetKind = 'external'`), showing the
            // "Dieser Linktyp wird von dieser Version nicht unterstützt"
            // hint on the single most common case. Ordered before the
            // catch-all now, same as the `whiteboard`/`document` branch
            // above it.
            this._linkKind = 'external';
        } else if (target) {
            // Anything else with a real target this dialog has no radio
            // option for: an UnknownLinkTarget's `kind`, OR a
            // KnowledgeObjectLinkTarget whose `knowledgeObjectType` has no
            // picker yet (e.g. 'image') — same hint either way.
            this._unknownTargetKind = target instanceof KnowledgeObjectLinkTarget ? target.knowledgeObjectType : target.kind;
            this._linkKind = 'external';
        } else {
            // No target at all (new tile, or an existing one that was
            // already link-less) — "Keinen Link setzen", not "Externe URL"
            // with a blank field.
            this._linkKind = 'none';
        }

        if (this._linkKind === 'whiteboard' || this._linkKind === 'document') {
            await this._loadKnowledgeObjectItems(this._linkKind, target);
        }
    }

    /**
     * Fetches the picker list for `kind` and preselects the current `target`
     * in it if it's a `KnowledgeObjectLinkTarget` of that same kind. Called
     * both from `willUpdate` (loading a `tile`) and from
     * `_onLinkKindChanged` (the user switches the radio) — lazily, only for
     * whichever kind is actually shown, rather than always eagerly
     * prefetching both lists on every dialog open.
     */
    private async _loadKnowledgeObjectItems(kind: 'whiteboard' | 'document', target: LinkTile['target'] | null): Promise<void> {
        this._pickerLoadGeneration += 1;
        const myGeneration = this._pickerLoadGeneration;
        this._koPickerLoading = true;
        try {
            const items = await container.resolve(KnowledgeObjectRepository).findAllFiltered({type: kind});
            if (myGeneration !== this._pickerLoadGeneration) {
                // A newer load (a further tile/kind change) has since replaced this one — discard.
                return;
            }
            this._koItems = items.map((ko: KnowledgeObject): ComboboxItem => ({
                id: ko.uid,
                title: ko.toString(),
                payload: ko,
            }));
            if (target instanceof KnowledgeObjectLinkTarget && target.knowledgeObjectType === kind) {
                const preselected = this._koItems.find((item) => item.id === target.uid) ?? null;
                this._selectedKoItem = preselected;
                if (!preselected) {
                    this._orphanedKoTitle = target.uid;
                }
            }
        } catch (error) {
            if (myGeneration !== this._pickerLoadGeneration) return;
            console.error(`Failed to load the ${kind} picker list`, error);
            this._koItems = [];
            UiHelper.notify({
                level: 'error',
                title: 'Fehler',
                content: html`<p>Die Liste konnte nicht geladen werden.</p>`,
                closable: true,
            });
        } finally {
            if (myGeneration === this._pickerLoadGeneration) {
                this._koPickerLoading = false;
            }
        }
    }

    /**
     * The concrete picker for whichever `_linkKind` is currently selected —
     * hierarchically belongs under the 3 radio buttons that choose the
     * kind, so it is passed into `<ha-input-radio-group>`'s `content`
     * property (rendered inside its own `.wrapper`, below the items)
     * instead of sitting as a sibling in this template.
     *
     * IMPORTANT — every event handler referenced from INSIDE this returned
     * template MUST be wrapped in an arrow function (`@value-changed=${(e) =>
     * this._onXyzChanged(e)}`), never passed as a bare method reference
     * (`@value-changed=${this._onXyzChanged}`) — review-fund, 2026-09-08,
     * Anke's bug report: "Speichern bleibt disabled, wenn ich Whiteboard oder
     * Textdokument auswähle", confirmed live via the browser console: the
     * `<ha-combobox>` picked up a click correctly (its OWN internal
     * `_selectedComboboxItem` was set, the picked title even displayed), but
     * this dialog's own `_selectedKoItem` stayed `null` forever, with no
     * console error.
     *
     * Root cause: this method's ENTIRE returned template is not part of THIS
     * dialog's own `render()` output — it is handed to `<ha-input-radio-
     * group>` as its `content` PROPERTY (see `_renderMainContent()` below)
     * and gets instantiated/rendered by THAT component's own `render()`
     * pass instead. lit-html's `EventPart` invokes a bare function listener
     * as `listener.call(options.host, event)` (`EventPart.handleEvent()` in
     * lit-html's source) — and `options.host` belongs to whichever
     * component's `render()` call actually processed the template subtree,
     * which here is `<ha-input-radio-group>`, not this dialog. A bare
     * `@value-changed=${this._onKoItemChanged}` therefore ran with `this`
     * silently bound to the RADIO GROUP instance instead of this dialog —
     * `this._selectedKoItem = ...` was quietly setting an unused property on
     * `<ha-input-radio-group>`, never touching this dialog's own `@state()`.
     * An arrow function closes over the correct `this` lexically at template-
     * creation time, independent of lit-html's `options.host` resolution, so
     * it stays correct no matter which component ends up actually rendering
     * the template. Handlers used directly in `_renderMainContent()`'s OWN
     * top-level template (`_onTextChanged`, `_onColorChanged`, `_onIconChanged`,
     * `_onImageChanged`, `_onLinkKindChanged` itself) are NOT affected — those
     * templates ARE this dialog's own `render()` output, so `options.host`
     * already resolves correctly there.
     */
    private _renderLinkKindContent(): HTMLTemplateResult {
        return html`
            ${when(this._unknownTargetKind, () => html`
                <p class="input-helptext">Dieser Linktyp wird von dieser Version nicht unterstützt —
                    Speichern ersetzt ihn.</p>
            `)}
            ${this._linkKind === 'none'
                ? html`
                    <p class="input-helptext">Die Kachel zeigt nur Text/Bild — ohne Link, nicht anklickbar.</p>
                `
                : this._linkKind === 'external'
                    ? html`
                        <ha-input-text data-type="url" class="max9999-flexrow"
                                       label=${translate(`linkview.dialog.link-tile-edit.label-url`)}
                                       placeholder="https://…"
                                       .value=${this._url}
                                       @value-changed=${(event: CustomEvent<{ value: string }>) => this._onUrlChanged(event)}
                        ></ha-input-text>
                    `
                    : html`
                        <!--
                            .value feeds HaCombobox's raw payload (a KnowledgeObject entity),
                            NOT the _selectedKoItem ComboboxItem wrapper itself. HaCombobox's
                            own value setter matches candidates via item.payload === value —
                            handing it the wrapper never matched, which silently reset
                            HaCombobox's internal _selectedComboboxItem to null on every
                            re-render right after picking an item (bug: the combobox looked
                            selected only by accident, via a stale value fallback path in
                            _renderLabel(), while its internal selection state was already
                            corrupted — see HaCombobox.render()'s "'id' in this.value" branch).
                            Passing the payload keeps both sides on the same "Entity | null"
                            contract value actually expects.
                        -->
                        <ha-combobox data-type="knowledge-object" class="max9999-flexrow"
                                     label=${translate(this._linkKind === 'document'
                                         ? `linkview.dialog.link-tile-edit.label-rte-document`
                                         : `linkview.dialog.link-tile-edit.label-whiteboard`)}
                                     placeholder="Bitte wählen…"
                                     ?readonly=${this._koPickerLoading}
                                     .items=${this._koItems}
                                     .value=${(this._selectedKoItem?.payload ?? null) as unknown as never}
                                     @value-changed=${(event: CustomEvent<{ value: ComboboxItem | null }>) => this._onKoItemChanged(event)}
                                     with-clear-button
                        ></ha-combobox>
                        ${when(this._orphanedKoTitle, () => html`
                            <p class="input-helptext">Das bisher verknüpfte
                                ${this._linkKind === 'document' ? 'Dokument' : 'Whiteboard'} existiert nicht mehr
                                — bitte ein anderes auswählen.</p>
                        `)}
                    `}
        `;
    }

    protected _renderMainContent(): HTMLTemplateResult {
        return html`
            <ha-form-link-tile>
                <header class="formular__header">
                    ${this._renderFormFooter()}
                </header>
                <main class="formular__main">
                    <ha-input-icon data-type="icon" class="max9999-flexrow"
                                   label=${translate(`linkview.dialog.link-tile-edit.label-icon`)}
                                   .items=${LINK_TILE_ICONS}
                                   .value=${this._icon}
                                   @value-changed=${this._onIconChanged}
                    ></ha-input-icon>
                    <!-- ha-rte has no built-in label (unlike ha-input-*, see
                         its own render()) — the label is rendered here by
                         hand, matching the exact <label> markup ha-input-text
                         used to render internally for this same field. Label
                         and editor sit together in
                         div.texteditor.max9999-flexrow — that wrapper now
                         carries the row class the sibling fields use, while
                         the editor itself carries class="wrapper"
                         (user, 2026-09-19/20). -->
                    <div class="texteditor max9999-flexrow">
                        <label for="link-tile-text">${translate(`linkview.dialog.link-tile-edit.label-text`)}</label>
                        <ha-rte id="link-tile-text" data-type="text" class="wrapper"
                                .value=${this._text}
                                @value-changed=${this._onTextChanged}
                        ></ha-rte>
                    </div>
                    <ha-input-color data-type="color" class="max9999-flexrow"
                                    label=${translate(`linkview.dialog.link-tile-edit.label-color`)}
                                    .value=${this._color}
                                    @value-changed=${this._onColorChanged}
                    ></ha-input-color>
                    <ha-input-file data-type="image" class="max9999-flexrow"
                                   label=${translate(`linkview.dialog.link-tile-edit.label-image`)}
                                   helper-text=${`Maximal ${MAX_ASSET_UPLOAD_MIB} MiB — PNG, JPEG, WebP oder GIF.`}
                                   @value-changed=${this._onImageChanged}
                    ></ha-input-file>
                    <ha-input-radio-group data-type="link-kind" class="max9999-flexrow"
                                          label=${translate(`linkview.dialog.link-tile-edit.label-link-kind`)}
                                          .items=${LINK_KIND_ITEMS}
                                          .value=${this._linkKind}
                                          .content=${this._renderLinkKindContent()}
                                          @value-changed=${this._onLinkKindChanged}
                    ></ha-input-radio-group>
                </main>
            </ha-form-link-tile>
        `;
    }

    protected _renderFormFooter(): HTMLTemplateResult {
        return html`
            <div class="button-group">
                <button type="button"
                        class="save buttonform-marker primary"
                        ?disabled=${!this._isSubmitEnabled}
                        @click=${this._onClickSave}

                        data-label=${translate(`general.save`)}><div class="hover-effect"></div>
                </button>
                <button type="button"
                        class="cancel buttonform-marker secondary"
                        @click=${this._onClickCancel}

                        data-label=${translate(`linkview.dialog.link-tile-edit.button-cancel`)}><div class="hover-effect"></div>
                </button>
            </div>
        `;
    }

    private _onTextChanged(event: CustomEvent<{ value: RteValue }>): void {
        this._text = event.detail.value ?? RteValueHelper.empty();
    }

    private _onLinkKindChanged(event: CustomEvent<{ value: LinkKindOption }>): void {
        const kind = event.detail.value ?? 'none';
        this._linkKind = kind;
        // Clear the PREVIOUS kind's picker state immediately — otherwise the
        // combobox would show stale whiteboard items while a document fetch
        // (or vice versa) is still in flight below.
        this._koItems = [];
        this._selectedKoItem = null;
        this._orphanedKoTitle = null;
        // Review Lauf 03, MAJOR: also clear the "unsupported target kind"
        // hint — it's set once in `willUpdate` from the LOADED tile's target
        // and otherwise never revisited, so switching the radio away from
        // "external" (even to a kind with a valid selection) left a now-false
        // "not supported" warning on screen.
        this._unknownTargetKind = null;
        if (kind === 'whiteboard' || kind === 'document') {
            void this._loadKnowledgeObjectItems(kind, this.tile?.target ?? null);
        }
    }

    private _onUrlChanged(event: CustomEvent<{ value: string }>): void {
        this._url = event.detail.value ?? ``;
    }

    private _onKoItemChanged(event: CustomEvent<{ value: ComboboxItem | null }>): void {
        this._selectedKoItem = event.detail.value ?? null;
        this._orphanedKoTitle = null;
    }

    private _onColorChanged(event: CustomEvent<{ value: string }>): void {
        this._color = event.detail.value ?? DEFAULT_TILE_COLOR;
    }

    private _onIconChanged(event: CustomEvent<{ value: string | null }>): void {
        this._icon = event.detail.value ?? null;
    }

    /**
     * Takes the picked file — or refuses it here, BEFORE anything is uploaded.
     *
     * The limit is the server's own, read from one constant that also feeds the
     * hint text above, so the number the user is shown and the number that is
     * enforced cannot drift apart. The type check is not the security control
     * (the server measures the bytes and would reject a mislabelled file
     * anyway); it exists so an SVG — which a browser EXECUTES, and which the
     * endpoint therefore refuses — is named as a refusal now rather than
     * surfacing later as an unexplained failure in the middle of a save.
     */
    private async _onImageChanged(event: CustomEvent<{ value: File | null }>): Promise<void> {
        const file = event.detail.value;
        if (!file) {
            this._clearImage();
            return;
        }

        if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
            this._clearImage();
            this._notifyImageRefused('Dieses Dateiformat wird nicht unterstützt — bitte PNG, JPEG, WebP oder GIF.');
            return;
        }

        if (file.size > MAX_ASSET_UPLOAD_BYTES) {
            this._clearImage();
            this._notifyImageRefused(`Das Bild ist zu groß — maximal ${MAX_ASSET_UPLOAD_MIB.toString()} MiB.`);
            return;
        }

        this._imageFile = file;
        // A newly picked file REPLACES whatever was stored before, so the old
        // reference must go — otherwise saving would keep the previous image.
        this._imageAssetUid = null;
        this._imageDataUrl = await this._readFileAsDataUrl(file);
    }

    private _clearImage(): void {
        this._imageDataUrl = null;
        this._imageFile = null;
        this._imageAssetUid = null;
    }

    private _notifyImageRefused(message: string): void {
        UiHelper.notify({
            level: 'error',
            title: 'Bild nicht übernommen',
            content: html`<p>${message}</p>`,
            closable: true,
        });
    }

    private _readFileAsDataUrl(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }

    private _onClickSave(): void {
        // The editor reports a change 300 ms after the last keystroke; fetch it now, before the
        // check below and the draft read `this._text`.
        this.querySelector<HaRteComponent>('ha-rte')?.flushPendingChange();
        if (!this._isSubmitEnabled) {
            return;
        }

        const target = this._linkKind === 'none'
            ? null
            : (this._linkKind === 'whiteboard' || this._linkKind === 'document')
                ? (this._selectedKoItem
                    ? new KnowledgeObjectLinkTarget(this._selectedKoItem.id as string, this._linkKind)
                    : null)
                : (this._url.trim() === `` ? null : new ExternalLinkTarget(this._url));

        // color always carries a real value (a native <input type="color">
        // can never be truly "unset") — persisted as picked, even when it's
        // still just the dialog's own default. text's own colour/background
        // formatting lives inside its own rich-text value now (the RTE's
        // colour/highlight toolbar — the full toolbar since the RTE
        // unification, 2026-09-14, not just the earlier minimal variant),
        // not as a separate field.
        const draft: LinkTileDraft = {
            text: this._text,
            target,
            imageDataUrl: this._imageDataUrl,
            imageFile: this._imageFile,
            imageAssetUid: this._imageAssetUid,
            color: this._color,
            icon: this._icon,
        };

        this.dispatchCommand<LinkTileDraft>({
            command: DialogCommandName.OKAY,
            payload: draft,
        });
        this._close();
    }

    private _onClickCancel(): void {
        this._close();
    }

    protected onClickClose(): void {
        DialogHelper.closeDialog(this.tagName);
        super.onClickClose();
    }

    private _close(): void {
        DialogHelper.closeDialog(this.tagName);
        this.dispatchCommand({
            command: DialogCommandName.CLOSE,
            payload: void 0,
        });
    }
}
